import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { allRows, useAccess } from './api/data';
import { useQuery } from '@tanstack/react-query';
import { command, getObject, name, type Row } from './api/client';
import { Select, Button, Empty, ErrorState, Field, Loading, useToast } from './ui';

export function CandidateMergePage() {
  const access = useAccess();
  const navigate = useNavigate();
  const client = useQueryClient();
  const toast = useToast();
  const list = useQuery({
    queryKey: ['candidate-merge-choices'],
    enabled: access.can('candidates.merge'),
    queryFn: ({ signal }) => allRows('/api/v1/candidates', {}, signal),
  });
  const [primary, setPrimary] = useState('');
  const [duplicates, setDuplicates] = useState<string[]>([]);
  const [reason, setReason] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  if (access.isLoading || list.isLoading) return <Loading />;
  if (!access.can('candidates.merge')) return <Empty title="Недостаточно прав" />;
  if (list.error) return <ErrorState error={list.error} />;
  return (
    <section className="form-page">
      <h1>Объединение кандидатов</h1>
      <p className="muted">
        Выберите основную карточку и её дубликаты. Отклики и данные дубликатов будут перенесены в
        основную карточку.
      </p>
      <form
        className="domain-form"
        onSubmit={async (event) => {
          event.preventDefault();
          setPending(true);
          setError(null);
          try {
            if (!duplicates.length) throw new Error('Выберите хотя бы один дубликат');
            const current = await getObject<Row>('/api/v1/candidates/{id}', { id: primary });
            const result = await command('POST', '/api/v1/candidates/merge', {
              primaryCandidateId: primary,
              mergedCandidateIds: duplicates,
              version: current.version,
              reason,
            });
            await client.invalidateQueries();
            toast('Карточки объединены');
            void navigate({
              to: '/$section/$resource/$entityId',
              params: {
                section: 'recruitment',
                resource: 'candidates',
                entityId: result.id || primary,
              },
            });
          } catch (cause) {
            setError(cause as Error);
          } finally {
            setPending(false);
          }
        }}
      >
        <Field label="Основная карточка">
          <Select
            name="primaryCandidateId"
            required
            value={primary}
            onChange={(event) => {
              setPrimary(event.target.value);
              setDuplicates((values) => values.filter((id) => id !== event.target.value));
            }}
          >
            <option value="" disabled>
              Выберите кандидата
            </option>
            {list.data?.map((candidate) => (
              <option key={candidate.id} value={candidate.id}>
                {name(candidate)} · {String(candidate.primaryEmail || '')}
              </option>
            ))}
          </Select>
        </Field>
        <fieldset className="form-section">
          <legend>Дубликаты</legend>
          <div className="filter-options candidate-merge-options">
            {list.data
              ?.filter((candidate) => candidate.id !== primary)
              .map((candidate) => (
                <label key={candidate.id}>
                  <input
                    type="checkbox"
                    name={`duplicate-${candidate.id}`}
                    checked={duplicates.includes(candidate.id)}
                    onChange={(event) =>
                      setDuplicates((values) =>
                        event.target.checked
                          ? [...values, candidate.id]
                          : values.filter((id) => id !== candidate.id),
                      )
                    }
                  />
                  <span>
                    {name(candidate)}
                    <span className="muted">{String(candidate.primaryEmail || '')}</span>
                  </span>
                </label>
              ))}
          </div>
        </fieldset>
        <Field label="Причина">
          <textarea
            name="reason"
            required
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </Field>
        {error && <ErrorState error={error} />}
        <footer>
          <Button
            onClick={() =>
              void navigate({
                to: '/$section/$resource',
                params: { section: 'recruitment', resource: 'candidates' },
              })
            }
          >
            Отмена
          </Button>
          <Button
            type="submit"
            className="primary"
            disabled={pending || !primary || !duplicates.length}
          >
            {pending ? 'Объединение…' : 'Объединить карточки'}
          </Button>
        </footer>
      </form>
    </section>
  );
}
